import {Type} from '@angular/core';

export function isPlainObject($: any): $ is object {
    return typeof $ === 'object'
        && $ !== null
        && !Array.isArray($);
}

export function isNullOrUndefined($v: any): boolean {
    return $v === null || $v === undefined;
}

export function fastObjectEquals($a: object | null, $b: object | null): boolean {
    if (isPlainObject($a) && isPlainObject($b) && $a !== $b) {
        const aKeys = Object.keys($a);
        for (const aKey of aKeys) {
            // Since this is fast comparison, only top level is supported.
            if (!$b.hasOwnProperty(aKey) || $a[aKey] !== $b[aKey]) {
                return false;
            }
        }
        return true;
    }
    return $a === $b;
}

export const EMPTY_DIFF = {};

export function diff<T>($a: T, $b: T, $deep: boolean = false): Partial<T> {
    if ($a === $b || (isNullOrUndefined($a) && isNullOrUndefined($b))) {
        return EMPTY_DIFF;
    }

    if (isNullOrUndefined($a) && !isNullOrUndefined($b)) {
        return {... ($b as any as object)};
    }

    const r: Partial<T> = {};
    if (!isNullOrUndefined($a) && isNullOrUndefined($b)) {
        for (const aKey of Object.keys($a)) {
            r[aKey] = {};
        }
        return r;
    }

    const abCommonKeys: string[] = [];
    for (const bKey of Object.keys($b)) {
        if ($a.hasOwnProperty(bKey)) {
            abCommonKeys.push(bKey);

            if (!maybeEquals($a[bKey], $b[bKey], $deep)) {
                r[bKey] = $b[bKey];
            }
        } else {
            r[bKey] = $b[bKey];
        }
    }

    for (const aKey of Object.keys($a)) {
        if (abCommonKeys.indexOf(aKey) === -1) {
            r[aKey] = {};
        }
    }

    return r;
}

export function maybeEquals($a: any, $b: any, $deep: boolean = true): boolean {
    let result = $a === $b;

    if (!result) {
        if (Array.isArray($a) && Array.isArray($b)) {
            result = $a.length === $b.length && !$a.some(($av, $idx) => !maybeEquals($av, $b[$idx], $deep));
        } else if (typeof $a === 'object' && typeof $b === 'object' && $a !== null && $b !== null) {
            // If both of them are empty arrays or empty objects, then consider as equal
            if (Object.keys($a).length === 0 && Object.keys($b).length === 0) {
                result = true;
            } else if ($deep) {
                result = Object.keys(diff($a, $b, $deep)).length === 0;
            }
        }
    }

    return result;
}

export function downloadFile(blob: Blob, name: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = name;
    link.href = url;
    link.className = 'hidden';
    document.body.appendChild(link);

    link.onclick = function () {
        requestAnimationFrame(function () {
            URL.revokeObjectURL(url);
            setTimeout(() => link.remove(), 300);
        });
    };

    link.click();
}

export  function  byAcceptedExtensionsFilter($f: File, fileExtensions: string): boolean {
    return !!$f && (
        !fileExtensions
        || !fileExtensions.length
        || !!fileExtensions.split(',').find($ext => $f.name && $f.name.toLowerCase().endsWith($ext.charAt(0) === '.' ? $ext : `.${$ext}`))
    );
}

export function generateTagNameForComponent($class: Type<any>): string {
    return 'app' + $class.name.replace(/component$/i, '').replace(/[A-Z]/g, $ => '-' + $.toLowerCase())
}
